/**
 * EvenementForm — formulaire de création/édition d'un événement dans une modale.
 */
import { useState, useEffect } from "react";
import Modal from "./Modal";
import Input from "./Input";
import Select from "./Select";
import Button from "./Button";
import { colors, fonts } from "../styles/tokens";

const TYPES = [
  { value: "culte",      label: "⛪ Culte" },
  { value: "reunion",    label: "🤝 Réunion" },
  { value: "retraite",   label: "🌿 Retraite" },
  { value: "bapteme",    label: "💧 Baptême" },
  { value: "formation",  label: "📖 Formation" },
  { value: "conference", label: "🎤 Conférence" },
  { value: "mission",    label: "✝ Mission / Évangélisation" },
  { value: "autre",      label: "Autre" },
];

const EMPTY = {
  titre: "",
  type: "culte",
  date_debut: "",
  date_fin: "",
  lieu: "",
  description: "",
  eglise_id: "",
};

export default function EvenementForm({
  open,
  onClose,
  onSubmit,
  eglises = [],
  initial = null,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        ...EMPTY,
        ...initial,
        eglise_id: initial.eglise_id ?? "",
        date_debut: initial.date_debut ? toLocalInput(initial.date_debut) : "",
        date_fin:   initial.date_fin   ? toLocalInput(initial.date_fin)   : "",
      });
    } else {
      // Pas de pré-sélection : l'utilisateur doit choisir l'église explicitement
      setForm({ ...EMPTY });
    }
    setErrors({});
    setGlobalError(null);
  }, [open, initial, eglises]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  /** Validation côté client. */
  const validate = () => {
    const errs = {};
    if (!form.titre.trim())     errs.titre = "Titre requis";
    if (!form.type)             errs.type = "Type requis";
    if (!form.date_debut)       errs.date_debut = "Date de début requise";
    if (!form.eglise_id)        errs.eglise_id = "Veuillez sélectionner une église";
    if (form.date_fin && form.date_debut && form.date_fin < form.date_debut) {
      errs.date_fin = "Doit être après le début";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        titre: form.titre.trim(),
        type: form.type,
        date_debut: new Date(form.date_debut).toISOString(),
        date_fin: form.date_fin ? new Date(form.date_fin).toISOString() : null,
        lieu: form.lieu.trim() || null,
        description: form.description.trim() || null,
        eglise_id: Number(form.eglise_id),
      };

      const result = await onSubmit(payload);
      if (result?.ok === false) {
        setGlobalError(result.error || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      setGlobalError(err.message || "Erreur inattendue");
    } finally {
      setSubmitting(false);
    }
  };

  const eglisesOptions = eglises.map((eg) => ({
    value: String(eg.id),
    label: `${eg.nom}${eg.ville ? ` — ${eg.ville}` : ""}`,
  }));

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={initial ? "Modifier l'événement" : "Nouvel événement"}
      subtitle={initial ? `Modification de "${initial.titre}"` : "Planifiez une nouvelle activité de votre église"}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {initial ? "Enregistrer" : "Créer l'événement"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {globalError && (
          <div style={{
            background: "rgba(224,91,106,0.1)",
            border: `1px solid ${colors.rose}`,
            color: colors.rose,
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
          }}>
            {globalError}
          </div>
        )}

        <Input
          label="Titre"
          value={form.titre}
          onChange={set("titre")}
          placeholder="Culte du dimanche, retraite spirituelle…"
          required
          error={errors.titre}
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          <Select
            label="Type"
            value={form.type}
            onChange={set("type")}
            options={TYPES}
            placeholder={null}
            required
            error={errors.type}
          />
          <Select
            label="Église"
            value={form.eglise_id}
            onChange={set("eglise_id")}
            options={eglisesOptions}
            required
            error={errors.eglise_id}
            placeholder="Sélectionnez une église..."
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          <Input
            label="Date et heure de début"
            type="datetime-local"
            value={form.date_debut}
            onChange={set("date_debut")}
            required
            error={errors.date_debut}
          />
          <Input
            label="Date et heure de fin"
            type="datetime-local"
            value={form.date_fin}
            onChange={set("date_fin")}
            error={errors.date_fin}
          />
        </div>

        <Input
          label="Lieu"
          value={form.lieu}
          onChange={set("lieu")}
          placeholder="Grande salle, plage de Bassam…"
          icon="📍"
        />

        <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontFamily: fonts.body }}>
          <span style={{ fontSize: "13px", fontWeight: 500, color: colors.textMuted }}>
            Description
          </span>
          <textarea
            value={form.description}
            onChange={set("description")}
            rows={3}
            placeholder="Détails complémentaires (optionnel)…"
            style={{
              background: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: "10px",
              padding: "12px 14px",
              fontSize: "14px",
              outline: "none",
              fontFamily: fonts.body,
              resize: "vertical",
              minHeight: "80px",
              transition: "border-color 150ms",
            }}
            onFocus={(e) => { e.target.style.borderColor = colors.gold; }}
            onBlur={(e)  => { e.target.style.borderColor = colors.border; }}
          />
        </label>

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/** Convertit une ISO string en valeur compatible <input type="datetime-local"> (sans timezone). */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
